import {
  ApplicationRef,
  DestroyRef,
  inject,
  Injectable,
  isDevMode,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import * as fromGenerated from '../_generated';
import * as fromAuth from '../auth';
import { CHATS_SOCKET } from '../sockets.config';
import { ChatWithMessagesActions } from './store/chats.actions';
import { ChatsState } from './store/chats.reducer';

interface ApiError {
  message: string;
  name: string;
  response: {
    error: string;
    message: string;
    statusCode: number;
  };
  status: number;
}

@Injectable()
export class ChatsSocket {
  private readonly store = inject<Store<ChatsState>>(Store);
  private readonly appRef = inject(ApplicationRef);
  private readonly destroyRef = inject(DestroyRef);
  public readonly socket = inject(CHATS_SOCKET);

  constructor() {
    this.socket
      .fromEvent<
        {
          type: string;
          chatWithMessages: fromGenerated.ChatWithMessagesView;
        },
        'chat.message.answered'
      >('chat.message.answered')
      .pipe(
        takeUntilDestroyed(this.destroyRef),

        tap(({ chatWithMessages }) => {
          this.store.dispatch(
            ChatWithMessagesActions.receivedChatWithMessagesFromSocketSuccess({
              chatWithMessages,
            }),
          );
          this.appRef.tick();
        }),
      )
      .subscribe();

    this.socket
      .fromEvent<
        {
          type: string;
          chatWithMessages: fromGenerated.ChatWithMessagesView;
          errorCode: string;
        },
        'chat.message.error'
      >('chat.message.error')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ chatWithMessages, errorCode }) => {
          console.error(`Error received from socket, code "${errorCode}"`);
          this.store.dispatch(
            ChatWithMessagesActions.receivedChatWithMessagesFromSocketFailure({
              chatWithMessages,
              errorCode,
            }),
          );
          this.appRef.tick();
        }),
      )
      .subscribe();

    this.socket
      .fromEvent<ApiError, 'error'>('error')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((error) => {
          console.error(`${error.name}: ${error.message}`);
          if (
            error.status === 401 &&
            error.message.includes("didn't include jwt tokens in cookie")
          ) {
            console.warn('Refreshing tokens');
            this.store.dispatch(fromAuth.AuthActions.refreshTokens());
            this.appRef.tick();
          }
        }),
      )
      .subscribe();

    if (isDevMode()) {
      this.socket.onAny((event) => console.info('[ChatsSocket] %s', event));
    }
  }
}
